import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Col, FormGroup, Input, Row } from "reactstrap";

import { H4, MUIIcons, P } from "../../AbstractElements";
import Divider from "../../CommonElements/Divider";
import EllipsisWithTooltip from "../../CommonElements/EllipsisWithTooltip";
import FilterSliderModal from "../../CommonElements/FilterSliderModal";
import LabelTooltip from "../../CommonElements/LabelTooltip";
import MultiSelectDropdown from "../../CommonElements/MultiSelectDropdown";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  getDomainList,
  getFilterRolesList,
  getProficiencyList,
} from "../../ReduxToolkit/Reducers/MasterSlice";
import {
  getAddGroupLearningPlanById,
  getLearningPlanList,
} from "../../ReduxToolkit/Reducers/UserGroupSlice";
import {
  Dates,
  Domain,
  Duration,
  EndDate,
  JobRole,
  LearningPlan,
  Level,
  Select,
  StartDate,
} from "../../utils/Constant";
import useIsMobile from "../../utils/helper/responsive";

const LearningPlanContainer = ({
  addedLearningPlans,
  setAddedLearningPlans,
  userCardDetail,
  setUserCardDetail,
  learningPlanId,
}) => {
  const [selectedFilterValues, setSelectedFilterValues] = useState({});
  const [defaultFilterValues, setDefaultFilterValues] = useState({});
  const [proficiencyListData, setProficiencyListData] = useState(null);
  const [roleFilterList, setRoleFilterList] = useState([]);
  const [domainListData, setDomainListData] = useState([]);
  const [learningPlanOptions, setLearningPlanOptions] = useState([]);
  const [preSelectedLearningPlanOptions, setPreSelectedLearningPlanOptions] =
    useState([]);
  const [userCardDetailIds, setUserCardDetailIds] = useState([]);
  const { proficiencyList, roleOnFilter, domainList } = useAppSelector(
    state => state.master
  );
  const { addUserGroupLearningPlanById } = useAppSelector(
    state => state.userGroup
  );
  const dispatch = useAppDispatch();
  const isMobile = useIsMobile();

  useEffect(() => {
    dispatch(getLearningPlanList());
    dispatch(getProficiencyList());
    dispatch(getFilterRolesList());
    dispatch(getDomainList());
  }, [dispatch]);

  useEffect(() => {
    if (learningPlanId) {
      dispatch(getAddGroupLearningPlanById({ id: learningPlanId }));
    }
  }, [learningPlanId]);

  useEffect(() => {
    const planData = [];
    addUserGroupLearningPlanById?.learning_plans?.map(item => {
      planData.push({
        name: item?.id?.toString(),
        value: item?.name,
        label: item?.name,
      });
    });
    setLearningPlanOptions(planData);
  }, [addUserGroupLearningPlanById?.learning_plans]);

  useEffect(() => {
    const levels = [];
    proficiencyList?.proficiency?.forEach(sites => {
      const userObj = {
        label: sites?.level_name,
        value: sites?.level_name,
        name: sites?.id.toString(),
      };
      levels.push(userObj);
    });
    setProficiencyListData(levels);
  }, [proficiencyList]);

  useEffect(() => {
    const value = [];
    roleOnFilter?.job_role?.forEach(item => {
      value.push({
        name: item?.id,
        label: item?.name,
        value: item?.name,
      });
    });
    setRoleFilterList(value);
  }, [roleOnFilter]);

  useEffect(() => {
    const domains = [];
    domainList?.domain?.forEach(element => {
      domains.push({
        name: element.id.toString(),
        value: element.domain_name,
        label: element.domain_name,
      });
    });
    setDomainListData(domains);
  }, [domainList]);

  const handleDone = async (values: { [key: string]: string[] }) => {
    if (values["domain"] || values["role"] || values["level"]) {
      await dispatch(
        getAddGroupLearningPlanById({
          id: learningPlanId,
          domain: values["domain"]?.toString(),
          role: values["role"]?.toString(),
          level: values["level"]?.toString(),
        })
      );
    }
  };

  const handleClear = async () => {
    setPreSelectedLearningPlanOptions([]);
    setUserCardDetailIds([]);
    setUserCardDetail([]);
    setUserCardDetailIds([]);
    await dispatch(getAddGroupLearningPlanById({ id: learningPlanId }));
    setSelectedFilterValues({});
    setDefaultFilterValues({});
  };

  return (
    <div className="learning-plan-container">
      <Row>
        <Col xs="11" sm="11" md="11" lg="7">
          <FormGroup>
            <LabelTooltip
              label={LearningPlan}
              tooltipText={`${Select} ${LearningPlan}`}
              important
            />
            <div>
              <MultiSelectDropdown
                onChange={value => {
                  const plansMap = [];

                  learningPlanOptions?.forEach((element: any) => {
                    if (value.includes(element.name)) {
                      plansMap.push({
                        name: element.name,
                        value: element.label,
                        label: element.label,
                      });
                    }
                  });
                  setPreSelectedLearningPlanOptions(plansMap);
                }}
                options={learningPlanOptions?.filter(item => {
                  return (
                    !addedLearningPlans.includes(Number(item?.name)) &&
                    !userCardDetailIds.includes(item?.name)
                  );
                })}
                placeholder={`${Select} ${LearningPlan}`}
                defaultSelected={preSelectedLearningPlanOptions}
                onBlurDropdown={values => {
                  const selected = [];
                  values?.forEach(item => {
                    selected.push(item?.name);
                  });

                  const planData = [];
                  const planIds = [];
                  addUserGroupLearningPlanById?.learning_plans?.forEach(
                    (element: any) => {
                      if (selected.includes(element?.id?.toString())) {
                        planIds.push(element?.id?.toString());
                        planData.push({
                          id: element?.id?.toString(),
                          name: element?.name,
                          duration: element?.duration,
                          start_date: new Date(),
                          end_date: new Date(
                            new Date().getTime() +
                              (element?.duration ?? 0) * 24 * 60 * 60 * 1000
                          ),
                          domain: element?.domains,
                          level: element?.level,
                        });
                      }
                    }
                  );
                  setUserCardDetailIds([...userCardDetailIds, ...planIds]);
                  setUserCardDetail([...userCardDetail, ...planData]);
                  setPreSelectedLearningPlanOptions([]);
                }}
              />
            </div>
          </FormGroup>
        </Col>
        <Col xs="1" sm="1" md="1" lg="1" className="filter-margin">
          <FilterSliderModal
            dropdowns={[
              {
                label: Domain,
                key: "domain",
                tooltipText: `${Select} ${Domain}`,
                options: domainListData,
                isMultiSelect: true,
              },
              {
                label: JobRole,
                key: "role",
                tooltipText: `${Select} ${JobRole}`,
                options: roleFilterList,
                isMultiSelect: false,
              },
              {
                label: Level,
                key: "level",
                tooltipText: `${Select} ${Level}`,
                options: proficiencyListData,
                isMultiSelect: true,
              },
            ]}
            selectedFilterValues={selectedFilterValues}
            defaultFilterValues={defaultFilterValues}
            setSelectedFilterValues={setSelectedFilterValues}
            setDefaultFilterValues={setDefaultFilterValues}
            onDone={handleDone}
            onClear={handleClear}
          />
        </Col>
      </Row>

      <div className="plan-details__container">
        {userCardDetail?.map((item, index) => {
          return (
            <>
              <Row className={isMobile ? "" : "px-4"} key={item.id}>
                <Col md="4" className="py-2">
                  <div className="d-flex align-items-center gap-3 h-100">
                    <Input
                      className="primary-checkbox"
                      id="flexCheckPublishLater"
                      type="checkbox"
                      checked={addedLearningPlans.includes(Number(item?.id))}
                      onChange={e => {
                        const checked = e.target.checked;
                        if (
                          checked &&
                          !addedLearningPlans.includes(Number(item.id))
                        ) {
                          setAddedLearningPlans([
                            ...addedLearningPlans,
                            Number(item.id),
                          ]);
                        } else if (!checked) {
                          setAddedLearningPlans(
                            addedLearningPlans.filter(
                              id => id !== Number(item.id)
                            )
                          );
                        }
                      }}
                    />
                    <div>
                      <H4 className="mb-2">{item?.name}</H4>
                      <div className="d-flex gap-2">
                        <P className="mb-0 d-flex gap-1">
                          {Domain} <div>:</div>
                          <EllipsisWithTooltip
                            textList={item?.domain?.map(item => {
                              return item.name ? item.name : item;
                            })}
                            idPrefix="tool-tip-domain"
                          />
                        </P>
                        <span>|</span>
                        <P className="mb-0">
                          {Level} : {item?.level}
                        </P>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col md="2" xs="12" sm="12" className="py-2">
                  <P className="gap-1 h-100 mb-0 d-flex align-items-center text-gray">
                    <MUIIcons size={19} iconName="TimerOutlined" />
                    {`${Duration} : ${item?.duration || 0}days`}
                  </P>
                </Col>
                <Col md="3" xs="12" sm="12">
                  <LabelTooltip
                    label={StartDate}
                    tooltipText={`${Select} ${StartDate}`}
                    important={true}
                    placement={"bottom"}
                  />
                  <div className={"w-100"}>
                    <DatePicker
                      selected={
                        item?.start_date
                          ? new Date(item?.start_date)
                          : new Date()
                      }
                      onChange={(date: Date | null) => {
                        const detail: any = [...userCardDetail];
                        detail[index].start_date = date;
                        detail[index].end_date = date;
                        setUserCardDetail(detail);
                      }}
                      className="form-control"
                      minDate={new Date()}
                      placeholderText={`${Select} ${Dates}`}
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={e => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </Col>

                <Col md="3" xs="12" sm="12">
                  <LabelTooltip
                    label={EndDate}
                    tooltipText={`${Select} ${EndDate}`}
                    important={true}
                    placement={"bottom"}
                  />
                  <div className={"w-100"}>
                    <DatePicker
                      selected={
                        item?.end_date ? new Date(item?.end_date) : new Date()
                      }
                      onChange={(date: Date | null) => {
                        const detail: any = [...userCardDetail];
                        detail[index].end_date = date;
                        setUserCardDetail(detail);
                      }}
                      minDate={item?.start_date || new Date()}
                      placeholderText={`${Select} ${Dates}`}
                      className="form-control"
                      dateFormat="dd-MM-yyyy"
                      disabledKeyboardNavigation={true}
                      onKeyDown={e => e.preventDefault()}
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                    />
                  </div>
                </Col>
              </Row>
              <Divider />
            </>
          );
        })}
      </div>
    </div>
  );
};

export default LearningPlanContainer;

LearningPlanContainer.propTypes = {
  learningPlanId: PropTypes.any,
  addedLearningPlans: PropTypes.arrayOf(PropTypes.number).isRequired,
  setAddedLearningPlans: PropTypes.func.isRequired,
  userCardDetail: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      duration: PropTypes.string,
      start_date: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
      ]),
      end_date: PropTypes.oneOfType([
        PropTypes.instanceOf(Date),
        PropTypes.string,
      ]),
      domain: PropTypes.string,
      level: PropTypes.string,
    })
  ).isRequired,
  setUserCardDetail: PropTypes.func.isRequired,
};
